import {FC, PropsWithChildren} from "react"
import {Link} from "react-router-dom"
import {ReactSVG} from "react-svg"

import {Button} from "@frontend/components"
import chevronRight from "@frontend/design/icons/chevron-right.svg"
import {useTranslation} from "@frontend/i18n"
import {makePath} from "@frontend/routing"
import {activeTheme} from "@frontend/themes/activeTheme"
import {emitHostCauseSelected} from "@frontend/utils/hostJourneyEvents"
import {trackEvent} from "@frontend/utils/trackEvent"
import {UiCauses} from "@ri2/app/server/routers/getUiCauses"
import {CauseOnMessageWithCauseAndPart} from "@ri2/db/client"
import {css, cx} from "@styled-system/css"
import {hstack, iconsColor, vstack} from "@styled-system/patterns"
import {Token} from "@styled-system/tokens"

import {CauseIcon} from "./cause-icon"

const {accentGlyph: homeVector} = activeTheme.assets

const styles = {
  // Unconfirmed rows sit on background.causesPanel (light RC / dark Bodewell slate).
  // Use causesPanel text tokens so titles/descriptions stay readable on both.
  notConfirmed: {
    descriptionColor: "text.causesPanel.muted",
    titleColor: "text.causesPanel.primary",
    borderColor: "border.causeCard",
    chevronColor: "foreground.brand.accent",
    paddingX: 8,
    iconBackground: "colors.background.primary" as const,
    iconColor: "foreground.primary" as const,
  },
  confirmed: {
    descriptionColor: "text.tertiary",
    titleColor: "text.primary",
    borderColor: "transparent",
    chevronColor: "foreground.brand.accent",
    paddingX: 12,
    iconBackground: "colors.background.tertiary" as const,
    iconColor: "foreground.primaryOnSolid" as const,
  },
}

interface Props {
  caseId: string
  cause: CauseOnMessageWithCauseAndPart
  bucket: keyof UiCauses
  className?: string
  setHoveredItem: (id: number | null) => void
  isHighlighted: boolean
  iconBackground: Token
  isInConversation?: boolean
  /** When true (widget mode), renders as a static card without navigation. */
  compact?: boolean
}

export const Cause: FC<Props> = ({
  caseId,
  cause,
  className,
  bucket,
  setHoveredItem,
  isHighlighted,
  isInConversation,
  iconBackground,
  compact,
}) => {
  const t = useTranslation()

  const style =
    bucket === "confirmed" ? styles["confirmed"] : styles["notConfirmed"]

  return (
    <div
      className={css(
        {
          padding: 1,
          position: "relative",
          overflowX: "visible",
          width: "100%",
          // Row surface: a plain divider on brands whose causes rail is a
          // flat list (radii/borderWidths/spacing resolve to 0 there), or a
          // fully bordered, chunked card on brands like Bodewell that define
          // real values for these tokens. `borderColor` already swaps to
          // "transparent" for confirmed rows below, so the side borders and
          // background stay invisible there without a separate branch.
          backgroundColor: "background.causesCard",
          borderStyle: "solid",
          // Shorthand borderColor — per-side *Color utilities for this token
          // aren't always emitted by Panda, which left borders on currentColor
          // (black). Widths stay per-side so RC/RI can keep a top-only divider
          // via causeCardSide: 0.
          borderColor: style.borderColor,
          borderTopWidth: 1,
          borderRightWidth: "causeCardSide",
          borderBottomWidth: "causeCardSide",
          borderLeftWidth: "causeCardSide",
          borderRadius: "causeCard",
          transition: "background-color 0.15s ease, border-color 0.15s ease",
          _hover:
            bucket === "confirmed"
              ? undefined
              : {
                  backgroundColor: "background.causesCardHover",
                  borderColor: "border.brand.accent",
                },
        },
        bucket === "confirmed" && {
          borderTopWidth: 0,
          borderRadius: 16,
          backgroundColor: "background.primary",
          zIndex: "causes.container",
          _hover: {
            bgGradient: cause.cause.isNormalOperation
              ? undefined
              : "causesHoverBorderGradient",
          },
        },
        isInConversation && {
          maxWidth: 540,
        },
      )}
      onMouseEnter={
        cause.cause.isNormalOperation
          ? undefined
          : (): void => {
              setHoveredItem(cause.id)
            }
      }
    >
      {isHighlighted &&
        bucket === "confirmed" &&
        !cause.cause.isNormalOperation && (
          <div
            className={css({
              position: "absolute",
              top: "-23.29px",
              right: "-24.93px",
              desktopOnly: {
                top: "-13.76px",
                right: "-14.76px",
              },
              zIndex: "causes.sparkle",
            })}
          >
            <ReactSVG
              src={homeVector}
              className={cx(
                iconsColor({color: "foreground.brand.accent"}),
                css({
                  "& svg": {
                    width: "49.86px",
                    height: "49.86px",
                    desktopOnly: {
                      width: "30.72px",
                      height: "30.72px",
                    },
                  },
                }),
              )}
            />
          </div>
        )}
      <LinkContainer
        caseId={caseId}
        cause={cause}
        className={className}
        bucket={bucket}
        compact={compact}
      >
        <CauseIcon
          color={
            bucket === "confirmed"
              ? (iconBackground ?? style.iconBackground)
              : style.iconBackground
          }
          className={css({flexShrink: 0, flexGrow: 0})}
          iconName={cause.cause.iconName}
          key={cause.cause.iconName}
          iconColor={style.iconColor}
        />
        <div
          className={vstack({
            gap: 0,
            alignItems: "flex-start",
            flexGrow: 1,
            width: "auto",
          })}
        >
          <h3
            className={css({
              color: style.titleColor,
              textStyle: "bodyMedium",
            })}
          >
            {cause.cause.name}
          </h3>
          <p
            className={css({
              color: style.descriptionColor,
              overflow: "hidden",
              textOverflow: "ellipsis",
              textStyle: "captionRegular",
              lineClamp: 2,
            })}
          >
            {cause.cause.description}
          </p>
        </div>
        {!cause.cause.isNormalOperation && !compact && (
          <div
            className={hstack({
              flexGrow: 1,
              justifyContent: "center",
              alignItems: "center",
              paddingY: 10,
            })}
          >
            {bucket === "confirmed" ? (
              <Button
                variant="primary"
                onClick={() => ({})}
                css={css.raw({
                  whiteSpace: "nowrap",
                  paddingX: 16,
                  desktopOnly: {},
                })}
                rightElement={
                  <ReactSVG
                    src={chevronRight}
                    className={hstack({
                      width: 20,
                      height: 20,
                      alignItems: "center",
                      justifyContent: "center",
                    })}
                  />
                }
              >
                <span
                  className={css({
                    "@container (max-Width: 300px)": {
                      display: "none",
                    },
                  })}
                >
                  {t(
                    isInConversation
                      ? "cases.case.viewMore"
                      : "cases.case.view",
                  )}
                </span>
              </Button>
            ) : (
              <ReactSVG
                src={chevronRight}
                className={cx(
                  hstack({
                    minWidth: 32,
                    width: 20,
                    height: 20,
                    justifyContent: "center",
                    alignItems: "center",
                  }),
                  iconsColor({
                    color: style.chevronColor ?? "base.white",
                  }),
                )}
              />
            )}
          </div>
        )}
      </LinkContainer>
    </div>
  )
}

interface LinkContainerProps {
  caseId: string
  cause: CauseOnMessageWithCauseAndPart
  bucket: keyof UiCauses
  className?: string
  compact?: boolean
}

const LinkContainer: FC<PropsWithChildren<LinkContainerProps>> = ({
  caseId,
  cause,
  bucket,
  className,
  compact,
  children,
}) => {
  const path = makePath({
    name: "cases",
    caseId,
    causeOnMessageId: cause.id,
  })

  const onClick = (): void => {
    trackEvent("chat_click_cause")
    emitHostCauseSelected(caseId, cause.id)
  }

  const style =
    bucket === "confirmed" ? styles["confirmed"] : styles["notConfirmed"]

  const c = cx(
    hstack({
      width: "100%",
      overflow: "hidden",
      paddingY: 16,
      paddingX: style.paddingX,
      gap: 12,
      position: "relative",
      alignItems: "center",
      boxSizing: "border-box",
    }),
    bucket === "confirmed" &&
      css({
        containerName: "cause",
        containerType: "inline-size",
        borderTopWidth: 0,
        borderRadius: 16,
        backgroundColor: "background.primary",
        position: "relative",
        zIndex: "causes.content",
        _hover:
          cause.cause.isNormalOperation || compact
            ? undefined
            : {
                borderStyle: "solid",
                bgGradient: "causesHoverGradient",
              },
      }),
    className,
  )

  // In compact/widget mode or for normal-operation causes, render a plain div
  return cause.cause.isNormalOperation || compact ? (
    <div className={c} data-test-id={`cause-card-${cause.id}`}>
      {children}
    </div>
  ) : (
    <Link
      className={c}
      to={path}
      replace
      onClick={onClick}
      data-test-id={`cause-card-${cause.id}`}
    >
      {children}
    </Link>
  )
}
